import { Dispatch } from 'redux'
import Papa from 'papaparse'

import { apiRequest } from '../actions/api'
import { API_POST_ZIP_FILE } from '../../consts'
import { CsvProduct } from '../../components/BatchProductCreator/BatchProductCreator'
import {
  PARSE_CSV_FILE,
  PARSE_CSV_FILE_FAILURE,
  PARSE_CSV_FILE_SUCCESS,
  POST_ZIP_FILE,
  POST_ZIP_FILE_FAILURE,
  POST_ZIP_FILE_SUCCESS,
  setParseCsvFileResult,
  setPostZipFileResult,
} from '../actions/batchProductCreator'
import {
  ParseCsvFileAction,
  ParseCsvFileSuccessAction,
  PostZipFileAction,
  PostZipFileSuccessAction,
} from '../types/batchProductCreator'

const postZipFileMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: PostZipFileAction) => {
    const result = next(action)

    if (action.type === POST_ZIP_FILE) {
      const { zipFile } = action.payload
      dispatch(
        apiRequest({
          url: API_POST_ZIP_FILE,
          method: 'post',
          headers: { 'content-type': 'application/zip' },
          entity: 'products',
          withCredentials: true,
          onSuccess: POST_ZIP_FILE_SUCCESS,
          onError: POST_ZIP_FILE_FAILURE,
          originalPayload: action.payload,
          data: zipFile,
        })
      )
    }

    return result
  }

const postZipFileSuccessMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: PostZipFileSuccessAction) => {
    const result = next(action)

    if (action.type === POST_ZIP_FILE_SUCCESS) {
      dispatch(setPostZipFileResult({ zipFileResult: action.payload.data }))
    }

    return result
  }

/**
 * Promise wrapper for Papa.parse to avoid having to deal with callbacks.
 */
const parseCsvPromise = (file: File, config: Omit<Papa.ParseConfig, 'complete' | 'error'> = {}) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, { ...config, complete: resolve, error: reject })
  })
}

const postCsvFileMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ParseCsvFileAction) => {
    const result = next(action)
    if (action.type === PARSE_CSV_FILE) {
      const { formData } = action.payload
      const file = formData.get('products')

      if (!(file instanceof File)) {
        dispatch({
          type: PARSE_CSV_FILE_FAILURE,
          payload: { errorMessage: 'File invalid, please select valid CSV file.' },
          meta: { originalPayload: action.payload },
        })
        return result
      }

      const parseResult = (await parseCsvPromise(file, {
        header: true,
      })) as Papa.ParseResult<CsvProduct>

      if (parseResult.data.length > 0 && parseResult.errors.length === 0) {
        dispatch({
          type: PARSE_CSV_FILE_SUCCESS,
          payload: { data: { products: parseResult.data } },
          meta: { originalPayload: action.payload },
        })
        return result
      } else {
        const errorMessage =
          parseResult.errors.length > 0
            ? undefined
            : 'CSV file is empty, no rows could be parsed from file.'
        dispatch({
          type: PARSE_CSV_FILE_FAILURE,
          payload: { errorMessage },
          meta: { originalPayload: action.payload },
        })
        return result
      }
    }

    return result
  }

const postCsvFileSuccessMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ParseCsvFileSuccessAction) => {
    const result = next(action)

    if (action.type === PARSE_CSV_FILE_SUCCESS) {
      dispatch(setParseCsvFileResult({ csvFileResult: action.payload.data }))
    }

    return result
  }

export const batchProductCreatorMiddleware = [
  postZipFileMiddleware,
  postZipFileSuccessMiddleware,
  postCsvFileMiddleware,
  postCsvFileSuccessMiddleware,
]
